import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ImportDataService } from 'app/core/services/import-data.service';
import { PrivateService } from 'app/core/services/private.service';
import { concatMap, delay, from, of, tap } from 'rxjs';

@Component({
  selector: 'modal-import-holiday',
  templateUrl: './modal-import-holiday.component.html',
  styleUrls: ['./modal-import-holiday.component.scss']
})
export class ModalImportHolidayComponent implements OnInit, AfterViewInit {

  isLoading: boolean = false;
  formatError: boolean = false;

  insertionMessage: string = null;
  totalScales: number = 0;
  isInserting = false;
  displayTable = true;
  isImported = false;

  @Input() importedData: any[] = [];
  selectedScales: any[] = [];
  dataSource = new MatTableDataSource<any>(this.importedData);

  displayedColumns: string[] = [
    'select', 'searchKey', 'name', 'category', 'region', 'province', 'dateFrom', 'dateTo'
  ];

  expectedHeaders: string[] = [
    'searchKey', 'name', 'category', 'region', 'province', 'dateFrom', 'dateTo'
  ];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(public activeModal: NgbActiveModal, private http: HttpClient, private importDataService: ImportDataService) { }

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource(this.importedData);
    this.selectedScales = [...this.importedData];

    if (!this.isDataFormatCorrect()) {
      this.formatError = true;
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  isDataFormatCorrect(): boolean {
    if (this.importedData.length === 0) return true;
    const importedHeaders = Object.keys(this.importedData[0]);
    return this.expectedHeaders.every(header => importedHeaders.includes(header));
  }

  onCheckboxChange(charge: any, isChecked: boolean): void {
    if (isChecked) {
      this.selectedScales.push(charge);
    } else {
      const index = this.selectedScales.indexOf(charge);
      if (index !== -1) {
        this.selectedScales.splice(index, 1);
      }
    }
  }

  isSelected(charge: any): boolean {
    return this.selectedScales.includes(charge);
  }

  toggleSelectAll(isChecked: boolean): void {
    if (isChecked) {
      this.selectedScales = [...this.importedData];
    } else {
      this.selectedScales = [];
    }
  }

  saveSelectedScales(): void {
    this.totalScales = this.selectedScales.length;
    let insertedCount = 0;
    this.isInserting = true;
    this.displayTable = false;
    this.isLoading = true;

    this.insertionMessage = "Please wait..."

    const convertedScales = this.selectedScales.map(charge => ({
      ...charge
    }));

    const scaleInsertions$ = from(convertedScales).pipe(
      concatMap((charge) =>
        of(charge).pipe(
          delay(3000),
          concatMap(emp =>
            this.importDataService.insertHolidayDetailsFromCSV(emp).pipe(
              tap({
                next: () => {
                  insertedCount++;
                  this.insertionMessage = `Inserting holiday details ${insertedCount} of ${this.totalScales}, please wait...`;
                },
                error: (err) => {
                  console.error(err);
                  this.insertionMessage = `Error inserting details for Holiday ID ${emp.chargeId}. Skipping...`;
                }
              })
            )
          )
        )
      )
    );

    scaleInsertions$.subscribe({
      next: () => {
        // This will run for each successful insertion
      },
      error: (err) => {
        console.error('An error occurred during the insertion process:', err);
        this.insertionMessage = `Error inserting holiday details. Please try again.`;
      },
      complete: () => {
        // All insertions are done
        if (insertedCount === this.totalScales) {
          this.insertionMessage = `All ${this.totalScales} holiday details inserted successfully.`;
          this.isImported = true;
        } else {
          this.insertionMessage += ` ${this.totalScales - insertedCount} insertion(s) failed.`;
        }
        this.isInserting = false;
        this.isLoading = false;
      }
    });
  }

  downloadTemplate(): void {
    const csvFilePath = 'assets/csv/Holiday_Data_Template.csv';

    this.http.get(csvFilePath, { responseType: 'blob' }).subscribe((data: Blob) => {
      const blob = new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = 'Holiday_Data_Template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    });
  }

  onClose(): void {
    this.activeModal.dismiss('cancel');
    window.location.reload();
  }
}
